<template>
    <section class="leafly-section bg-gray-100">
        <div class="container mx-auto px-3 pb-16">
            <div class="flex flex-col md:flex-row justify-between items-start ">
                <div class="block">
                    <h2 class="leafly-section-title">Explore Popular Packaging</h2>
                    <p class="leafly-section-second-title">By Category</p>
                </div>
                <div class="hidden md:block mt-[-12px] md:mt-0">
                    <nuxt-link :to="'/custom-packaging'" class="flex items-center text-green-600 font-bold hover:text-green-700 mt-4">
                        CUSTOM PACKAGING
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 200 200"
                            style="enable-background:new 0 0 200 200;width:24px;height:24px" xml:space="preserve">
                            <path fill="currentColor" d="M151.6,51.7l0,98.1c0,0.9-0.7,1.6-1.6,1.6c-0.4,0-0.8-0.2-1.1-0.5L147,149c-0.6-0.6-0.9-1.4-0.9-2.2l0-86.8l-90.5,90.6c-0.6,0.6-1.6,0.6-2.2,0l-2.1-2.1c-0.6-0.6-0.6-1.6,0-2.2l90.6-90.6l-86.8,0c-0.8,0-1.6-0.3-2.2-0.9l-1.9-1.9c-0.6-0.6-0.6-1.6,0-2.2c0.3-0.3,0.7-0.5,1.1-0.5l98.1,0C150.9,50.2,151.6,50.9,151.6,51.7L151.6,51.7z" />
                        </svg>
                    </nuxt-link>
                </div>
            </div>

            <!-- 分类选项卡导航 -->
            <div class="relative mt-6">
                <div class="category-tabs-container relative" ref="tabContainerRef">
                    <!-- Swiper 选项卡 -->
                    <Swiper 
                        :modules="tabModules"
                        :slides-per-view="'auto'"
                        :free-mode="true"        
                        :slides-per-group="1"
                        @swiper="onTabSwiperInit"
                        class="category-tabs-swiper"
                    >
                        <SwiperSlide 
                            v-for="(product, index) in productList" 
                            :key="index"
                            @mouseenter="handleTabHover(index)"
                            @mouseleave="resetIndicatorToActive"
                            class="tab-slide-wrapper"
                        >
                            <button 
                                @click="handleCategoryClick(index)"
                                class="whitespace-nowrap text-center fixed-width-tab h-[48px] leading-[48px] w-full" 
                                :class="[
                                    activeCategoryIndex === index && !isHovering ? 'active' : ''
                                ]"
                                :ref="el => setTabRef(el, index)"
                            >
                                {{ product.title }}
                            </button>
                        </SwiperSlide>
                    </Swiper>
                    <div 
                        class="tab-indicator"
                        :style="{
                            transform: `translateX(${indicatorLeft}px)`,
                            width: `${indicatorWidth}px`
                        }"
                        v-show="indicatorWidth > 0"
                    ></div>
                </div>
            </div>

            <!-- 产品展示区域 -->
            <div class="mt-8 relative">
                <!-- 并列的产品 Swiper，每个分类一个 -->
                <div v-for="(product, index) in productList" :key="index" v-show="activeCategoryIndex === index" class="product-container">
                    <div class="relative">
                        <!-- 左侧导航按钮 -->
                        <button 
                            :class="`swiper-button-prev-${index}`"
                            class="product-nav-btn product-nav-btn-prev"
                            aria-label="Previous"
                        >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <!-- 产品滑动容器 -->
                        <Swiper
                            :modules="productModules"
                            :slides-per-view="'auto'"
                            :space-between="20"
                            :navigation="{
                                prevEl: `.swiper-button-prev-${index}`,
                                nextEl: `.swiper-button-next-${index}`,
                            }"
                            @swiper="(swiper) => onProductSwiperInit(swiper, index)"
                            class="product-swiper px-12"
                            :grab-cursor="true"
                        >
                            <SwiperSlide 
                                v-for="(article, pIndex) in product.articleList" 
                                :key="pIndex"
                                class="!w-auto"
                            >   

                                <!-- ${100 + (pIndex<4 ? pIndex*300 : 0)}-->
                                <div 
                                    class="product-card bg-white rounded-lg overflow-hidden flex flex-col"
                                    :class="{ 'animate-fade-up': visibleCards.has(`${index}-${pIndex}`) }"
                                    :style="visibleCards.has(`${index}-${pIndex}`) ? `animation-delay: ${100 + (pIndex<4 ? pIndex*300 : 0) }ms` : ''"
                                    :ref="el => setCardRef(el, `${index}-${pIndex}`)"
                                >
                                    <!-- 产品图片 -->
                                    <div class="relative w-full pt-[100%] ">
                                        <img :src="article.imageUrl" :alt="article.title" class="absolute top-0 left-0 w-full h-full object-cover" loading="lazy" /> 
                                    </div>

                                    <!-- 产品信息 -->
                                    <div class="p-4 flex flex-col">
                                        <div class="h-[128px]">
                                            <h3 class="mb-2 line-clamp-2">
                                                <nuxt-link :to="article.fullUrl" class="leafly-article-item-title   line-clamp-2 font-size-22px hover:text-green-600">
                                                    {{ article.title }}
                                                </nuxt-link>
                                            </h3>
                                            <p class="mt-1 mb-4 line-clamp-3 leafly-article-item-content min-height-[3.6rem]">{{ article.description }}</p>
                                        </div>
                                        <div class="mt-auto pt-4">
                                            <nuxt-link :to="article.fullUrl" class="block w-full bg-green-600 text-white text-center py-2 rounded-md text-sm font-weight-400 hover:bg-green-700 transition-colors">
                                                View Product
                                            </nuxt-link>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        </Swiper>

                        <!-- 右侧导航按钮 -->
                        <button 
                            :class="`swiper-button-next-${index}`"
                            class="product-nav-btn product-nav-btn-next"
                            aria-label="Next"
                        >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup lang="ts">
    import cmsConfig from '~/cmsConfig';
    import cmsUtils from '~/utils/cmsUtils';
    import type { Category, Article, Page, Tags, ArticleListPo } from '~/types';
    import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { FreeMode, Mousewheel, Navigation } from 'swiper/modules';
    import type { Swiper as SwiperType } from 'swiper';
    import { useHttpBy$Fetch, useHttp } from '~/utils/httpUtil'

    // 分类数据的接口定义
    interface Product {
        title: string;
        categoryUrl: string;
        tags: string;
        articleList: Article[];
    }

    // Swiper 模块配置
    const tabModules = [Mousewheel, FreeMode]; // 使用标准页面滑动，支持鼠标滚轮和自由滑动
    const productModules = [Navigation];

    // Swiper 实例
    const tabSwiper = ref<SwiperType | null>(null);
    const tabContainerRef = ref<HTMLElement | null>(null);
    const tabRefs = ref<(HTMLElement | null)[]>([]);
    const indicatorLeft = ref(0);
    const indicatorWidth = ref(0);
    const productSwipers = ref<(SwiperType | null)[]>([]);

    // 选项卡响应式配置 - 大屏等分显示所有，小屏显示部分并可滑动
    const slidesPerView = 7; // 默认显示所有7个（等分）
    
    const productList = ref<Product[]>([
        {
            title: 'Child-Resistant',
            categoryUrl: 'packaging',
            tags: 'child-resistant',
            articleList: []
        },
        {
            title: 'Rigid',
            categoryUrl: 'rigid-boxes',
            tags: '',
            articleList: []
        },
        // {
        //     title: 'Folding Carton',
        //     categoryUrl: 'folding-carton-boxes',
        //     tags: '',
        //     articleList: []
        // },
        {
            title: 'Pouches',
            categoryUrl: 'mylar-bags',
            tags: '',
            articleList: []
        },
        {
            title: 'Jars',
            categoryUrl: 'jars',
            tags: '',
            articleList: []
        },
        {
            title: 'Tubes',
            categoryUrl: 'tubes',
            tags: '',
            articleList: []
        },
        {
            title: 'Tins',
            categoryUrl: 'tins',
            tags: '',
            articleList: []
        }
    ])

    const cmsInfo = await cmsUtils.getCmsInfo();

    await Promise.all(productList.value.map(async (item) => {
        const category = cmsUtils.getCategoryByUrl(item.categoryUrl, cmsInfo);
        const childCategoryIds = cmsUtils.getChildCategoryIds(category!.id, cmsInfo);
        const allCategoryIds = [category!.id, ...childCategoryIds];

        // 假设url 是 tags/pre-roll_child-registant ,这里有两个tags，分别需要获得他们的子tags，并封装到tagsIdsList中。
        const tagsList = cmsUtils.getTagsListByUrl(item.tags, cmsInfo)
        let tagsIdsList:number[][] = []
        tagsList.forEach(tags => {
            const childTagsIds = [tags!.id, ...cmsUtils.getChildTagsIds(tags!.id, cmsInfo)]
            tagsIdsList.push(childTagsIds)
        })

        // 如果传递的参数是下面这个二维数组，测试从数据库获取的数据是正确的。sql查询是没有问题的。
        // tagsIdsList = [[1,4,6],[208]] 
        
        const articleListPo: ArticleListPo = {
            "websiteId": cmsInfo!.website.id,
            "categoryIds": allCategoryIds,
            "tagsIdsList": tagsIdsList,
            "orderType":"top",
            "pageNum": 1,
            "pageSize": 8
        }

        const articlesPage = await useHttpBy$Fetch("/nuxt/article/list", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: articleListPo
        }) as Page<Article>;
        articlesPage.rows.forEach((article: Article) => {
            let articleCategory: Category = cmsInfo.category.find(item => item.id == article.categoryId)!;
            article.fullUrl = cmsUtils.appendCategoryUrl(articleCategory, cmsInfo, "/" + article.titleUrl );
        });
        item.articleList = articlesPage.rows;
    }));

    const activeCategoryIndex = ref(0)
    const isHovering = ref(false)

    // 动画相关变量
    const visibleCards = ref(new Set<string>())
    const cardRefs = new Map<string, HTMLElement>()
    const observer = ref<IntersectionObserver | null>(null)

    // Swiper 初始化回调
    const onTabSwiperInit = (swiper: SwiperType) => {
        tabSwiper.value = swiper;
    }

    const onProductSwiperInit = (swiper: SwiperType, index: number) => {
        productSwipers.value[index] = swiper;
    }

    // 处理分类选项卡点击
    function handleCategoryClick(index: number) {
        activeCategoryIndex.value = index;
        isHovering.value = false;
        updateIndicator(index);
    }

    // 悬停时移动游标
    function handleTabHover(index: number) {
        isHovering.value = true;
        updateIndicator(index);
    }

    // 鼠标移出后恢复到当前激活项
    function resetIndicatorToActive() {
        isHovering.value = false;
        updateIndicator(activeCategoryIndex.value);
    }

    function updateIndicator(index: number) {
        const el = tabRefs.value[index];
        const container = tabContainerRef.value;
        if (!el || !container) return;
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        indicatorLeft.value = elRect.left - containerRect.left;
        indicatorWidth.value = elRect.width;
    }

    // 设置卡片引用
    function setCardRef(el: Element | ComponentPublicInstance | null, cardId: string) {
        if (el && el instanceof HTMLElement) {
            cardRefs.set(cardId, el)
        } else {
            cardRefs.delete(cardId)
        }
    }

    function setTabRef(el: Element | ComponentPublicInstance | null, index: number) {
        tabRefs.value[index] = el instanceof HTMLElement ? el : null
    }

    // 创建 Intersection Observer
    function createObserver() {
        observer.value = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const cardId = entry.target.getAttribute('data-card-id')
                if (cardId) {
                    if (entry.isIntersecting) {
                        visibleCards.value.add(cardId)
                    }
                    // 注意：这里不移除已经显示过的卡片，让动画只触发一次
                }
            })
        }, {
            root: null,
            rootMargin: '0px 0px -100px 0px', // 提前100px触发动画
            threshold: 0.1
        })
    }

    // 观察所有卡片
    function observeCards() {
        if (!observer.value) return
        
        cardRefs.forEach((el, cardId) => {
            el.setAttribute('data-card-id', cardId)
            observer.value!.observe(el)
        })
    }

    // 初始化
    onMounted(() => {
        nextTick(() => {
            // 给DOM足够的时间来渲染产品列表
            setTimeout(() => {
                // 创建并启动 Intersection Observer
                createObserver();
                observeCards();
                // 初始化游标位置
                updateIndicator(activeCategoryIndex.value);
            }, 300);
        });

        // 监听窗口大小变化，实时更新指示条位置
        window.addEventListener('resize', () => updateIndicator(activeCategoryIndex.value));
    })

    watch(activeCategoryIndex, (idx) => {
        updateIndicator(idx);
    })

    onUnmounted(() => {
        window.removeEventListener('resize', () => updateIndicator(activeCategoryIndex.value));
    })

    onUnmounted(() => {
        // 清理 Intersection Observer
        if (observer.value) {
            observer.value.disconnect();
            observer.value = null;
        }
        cardRefs.clear();
    })

</script>

<style scoped lang="scss">
    .leafly-section {
        padding-bottom: 0px !important;
    }

    /* 选项卡容器样式 */
    .category-tabs-container {
        position: relative;
        width: 100%;
        overflow-x: hidden;
    }

    /* Swiper 样式覆盖 */
    .category-tabs-swiper {
        overflow: visible;
        
        :deep(.swiper-wrapper) {
            display: flex;
        }
        
        :deep(.swiper-slide) {
            flex:1;
            min-width: 120px;
            padding-left:2px;
            padding-right:2px;
            flex-shrink: 0;
        }
    }

    /* SwiperSlide 包装器样式，确保整个区域（包括间隔）都能响应 hover */
    .tab-slide-wrapper {
        display: flex;
        align-items: stretch;
        height: 100%;
    }

    /* 固定宽度选项卡样式 - 默认灰色 */
    .fixed-width-tab {
        position: relative;
        border-bottom-width: 1px;
        border-bottom-color: #595c60; /* 默认灰色边框 (gray-400) */
        border-bottom-style: solid;
        transition: color 0.3s, border-color 0.3s;
        cursor: pointer;
        width: 100%;
        display: block;
        font-size: 1.25rem;
        font-weight: 600;
    }

    /* Hover 状态 - 绿色 */
    .fixed-width-tab:hover {
        color: #059669; /* green-700 */
        border-bottom-color: #059669;
    }

    /* 选中状态 - 绿色 + 粗边框 */
    .fixed-width-tab.active {
        color: #059669; /* green-700 */
        border-bottom-width: 4px;
        border-bottom-color: #059669;
    }

    .tab-indicator {
        position: absolute;
        bottom: 0;
        height: 4px;
        background: #059669; /* green-700，深绿色 */
        border-radius: 9999px;
        transition: transform 0.25s ease, width 0.25s ease;
        pointer-events: none;
    }

    /* Swiper slide 样式 */
    .category-tabs-swiper :deep(.swiper-slide) {
        display: flex;
        justify-content: center;
    }

    /* 响应式字体大小 */
    @media (max-width: 640px) {
        .fixed-width-tab {
            font-size: 0.875rem;
        }
    }

    /* 产品容器样式 */
    .product-container {
        position: relative;
    }

    /* 产品 Swiper 样式 */
    .product-swiper {
        overflow: hidden;
        padding-bottom: 10px;
        
        :deep(.swiper-wrapper) {
            display: flex;
        }
    }

    /* 隐藏 Swiper 默认的导航按钮 */
    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
        display: none;
    }

    /* 自定义导航按钮样式 */
    .product-nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        width: 40px;
        height: 40px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        color: #059669;
        flex-shrink: 0;

        svg {
            width: 1rem;
            height: 1rem;
            stroke: #059669;
        }

        &:hover:not(:disabled) {
            background: #059669;
            color: white;
            border-color: #059669;
            
            svg {
                stroke: white;
            }
        }

        &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

    .product-nav-btn-prev {
        left: 12px;
    }

    .product-nav-btn-next {
        right: 12px;
    }

    /* 产品卡片样式 */
    .product-card {
        display: flex;
        flex-direction: column;
        width: 364px;
        flex-shrink: 0;
        background-color: white;
        border-radius: 10px;
        overflow: hidden;
        transition: box-shadow 0.3s ease;
        /* 初始状态：隐藏 */
        opacity: 0;
        transform: translateY(30px);
    }

    .product-card:hover {
        box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
    }

    /* 自定义淡入向上动画 */
    .product-card.animate-fade-up {
        animation: fadeUp 0.8s ease-out forwards;
    }

    @keyframes fadeUp {
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    /* 响应式产品卡片尺寸 */
    @media (max-width: 1024px) {
        .product-card {
            width: 260px;
            min-width: 260px;
        }
    }

    @media (max-width: 640px) {
        .product-card {
            width: 240px;
            min-width: 240px;
        }
    }

    /* 按钮容器固定在底部 */
    .product-card .mt-auto {
        margin-top: auto;
    }
</style>
